import { createContext, useContext } from "react";
import {
  ProfileStatus,
  useProfileStatusList,
} from "../data/useProfileStatusList";

interface ProfileStatusContextValues {
  statuses: ProfileStatus[] | undefined;
  isLoadingStatuses: boolean;
  getProfileStatusByLabel: (slug?: string) => ProfileStatus | undefined;
  getProfileStatusById: (id?: number) => ProfileStatus | undefined;
}

export enum ProfileStatusSlug {
  New = "new",
  InProgress = "in progress",
  RequiresReview = "requires review",
  SecondaryReview = "secondary review",
  PeriodicReview = "periodic review",
  Completed = "completed",
  Rejected = "rejected",
}

/** Context */
export const ProfileStatusContext =
  createContext<ProfileStatusContextValues | null>(null);

/** Provider */
export const ProfileStatusProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const { data, isLoading } = useProfileStatusList();

  const statuses: ProfileStatus[] = data?.data ?? [];

  const getProfileStatusByLabel = (label?: string) =>
    statuses.find((s) => s.label === label || s.status === label);

  const getProfileStatusById = (id?: number) =>
    statuses.find((s) => s.id === id);

  const value = {
    statuses,
    isLoadingStatuses: isLoading,
    getProfileStatusByLabel,
    getProfileStatusById,
  };

  return (
    <ProfileStatusContext.Provider value={value}>
      {children}
    </ProfileStatusContext.Provider>
  );
};

export const useProfileStatuses = () => {
  const ctx = useContext(ProfileStatusContext);
  if (!ctx) {
    throw new Error(
      "useProfileStatuses must be used within ProfileStatusProvider"
    );
  }
  return ctx;
};
